import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiV1 } from "../Auth/Interceptor";

const initialStateValue = {
    activeTab: 'all',
    products: {
        products: [],
        loading: 'idle',
        error: null,
    },
    modal: {
        activeModal: '',
        displayModal: false,
        modalSection: '',
    },
    activeProduct: {
        activeProduct: {},
        loading: 'idle',
        error: null,
    },
    filters: {
        selectedCategories: [],
        selectedCollections: [],
        selectedTags: [],
        sortType: 'newly-added'
    },
    productKebab: {
        kebabOpen: false,
        productId: null,
    },
    sizeChartKebab: {
        kebabOpen: false,
        sizeChartId: null,
    },
    selectedProduct: {},
    selectedVariant: {},
    variantInfo: {
        activeColorVariant: {
            activeImageList: [],
            color: '',
        },
        activeImageUrl: '',
        sizeList: [],
        activeSize: '',
        prices: {
            mrp: null,
            sp: null,
            discount: 0,
        }
    },
    sizeCharts: {
        sizeCharts: [],
        loading: 'idle',
        error: null,
    },
    selectedSizeChart: {},
    sizeChartDetails: {
        sizeChartDetails: [],
        loading: 'idle',
        error: null,
    },
}

const fetchInventoryProducts = createAsyncThunk('inventory/fetchInventoryProducts', async () => {
    try {
        const response = await apiV1.get(`inventory/getAll/product`);
        return response.data.data.products;
    } catch(error) {
        throw error;
    }
});

const fetchInventoryProduct = createAsyncThunk('inventory/fetchInventoryProduct', async (productId) => {
    try {
        const response = await apiV1.get(`inventory/${productId}`);
        return response.data.data;
    } catch(error) {
        throw error;
    }
});

const fetchSizeCharts = createAsyncThunk('inventory/fetchSizeCharts', async () => {
    try {
        const response = await apiV1.get(`size-chart`);
        return response.data.data;
    } catch(error) {
        throw error;
    }
});

const fetchSizeChart = createAsyncThunk('inventory/fetchSizeChart', async (chartId) => {
    try {
        const response = await apiV1.get(`size-chart/${chartId}`);
        return response.data.data;
    } catch(error) {
        throw error;
    }
});

const inventoryManagementSlice = createSlice({
    name: 'inventoryManagement',
    initialState: initialStateValue,
    reducers: {
        changeTab: (state, action) => {
            state.activeTab = action.payload;
        },
        setModal: (state, action) => {
            state.modal.activeModal = action.payload;
            state.modal.displayModal = true;
        },
        closeModal: (state, _) => {
            state.modal.activeModal = '';
            state.modal.displayModal = false;
            state.selectedSizeChart = {};
        },
        updateSelectedCategories: (state, action) => {
            state.filters.selectedCategories = action.payload;
        },
        updateSelectedCollections: (state, action) => {
            state.filters.selectedCollections = action.payload;
        },
        updateSelectedTags: (state, action) => {
            state.filters.selectedTags = action.payload;
        },
        updateSortType: (state, action) => {
            state.filters.sortType = action.payload;
        },
        openProductKebabMenu: (state, action) => {
            state.productKebab.kebabOpen = true;
            state.productKebab.productId = action.payload;
        },
        closeProductKebabMenu: (state, _) => {
            state.productKebab = initialStateValue.productKebab;
        },
        openSizeChartKebabMenu: (state, action) => {
            state.sizeChartKebab.kebabOpen = true;
            state.sizeChartKebab.sizeChartId = action.payload;
        },
        closeSizeChartKebabMenu: (state, _) => {
            state.sizeChartKebab = initialStateValue.sizeChartKebab;
        },
        setSelectedProduct: (state, action) => {
            state.selectedProduct = action.payload;
        },
        setSelectedVariant: (state, action) => {
            state.selectedVariant = action.payload;
        },
        removeProductFromInventory: (state, action) => {
            state.products.products = state.products.products.filter(product => product.product.id !== action.payload);
        },
        updateInventoryProducts: (state, action) => {
            state.products.products = action.payload;
        },
        setActiveImageList: (state, action) => {
            state.variantInfo.activeColorVariant = action.payload;
        },
        setActiveImageUrl: (state, action) => {
            state.variantInfo.activeImageUrl = action.payload;
        },
        setActiveSize: (state, action) => {
            state.variantInfo.activeSize = action.payload;
        },
        setSizeList: (state, action) => {
            state.variantInfo.sizeList = action.payload;
        },
        setPrices: (state, action) => {
            state.variantInfo.prices = action.payload;
        },
        setSelectedSizeChart: (state, action) => {
            state.selectedSizeChart = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(fetchInventoryProducts.pending, (state) => {
            state.products.loading = 'loading';
        })
        .addCase(fetchInventoryProducts.fulfilled, (state, action) => {
            state.products.loading = 'succeeded';
            state.products.products = action.payload;
        })
        .addCase(fetchInventoryProducts.rejected, (state, action) => {
            state.products.loading = 'failed';
            state.products.error = action.error.message;
        })
        .addCase(fetchInventoryProduct.pending, (state) => {
            state.activeProduct.loading = 'loading';
        })
        .addCase(fetchInventoryProduct.fulfilled, (state, action) => {
            state.activeProduct.loading = 'succeeded';
            state.activeProduct.activeProduct = action.payload;
            state.activeProduct.error = null;
        })
        .addCase(fetchInventoryProduct.rejected, (state, action) => {
            state.activeProduct.loading = 'failed';
            state.activeProduct.error = action.error.message;
        })
        .addCase(fetchSizeCharts.pending, (state) => {
            state.sizeCharts.loading = 'loading';
        })
        .addCase(fetchSizeCharts.fulfilled, (state, action) => {
            state.sizeCharts.loading = 'succeeded';
            state.sizeCharts.sizeCharts = action.payload;
            state.sizeCharts.error = null;
        })
        .addCase(fetchSizeCharts.rejected, (state, action) => {
            state.sizeCharts.loading = 'failed';
            state.sizeCharts.error = action.error.message;
        })
        .addCase(fetchSizeChart.pending, (state) => {
            state.sizeChartDetails.loading = 'loading';
        })
        .addCase(fetchSizeChart.fulfilled, (state, action) => {
            state.sizeChartDetails.loading = 'succeeded';
            state.sizeChartDetails.sizeChartDetails = action.payload;
            state.sizeChartDetails.error = null;
        })
        .addCase(fetchSizeChart.rejected, (state, action) => {
            state.sizeChartDetails.loading = 'failed';
            state.sizeChartDetails.error = action.error.message;
        });
    },
});

export const {
    changeTab,
    setModal,
    closeModal,
    updateSelectedCategories,
    updateSelectedCollections,
    updateSelectedTags,
    updateSortType,
    openProductKebabMenu,
    closeProductKebabMenu,
    setSelectedProduct,
    removeProductFromInventory,
    setActiveImageList,
    setActiveImageUrl,
    setActiveSize,
    setSizeList,
    setPrices,
    setSelectedSizeChart,
    openSizeChartKebabMenu,
    closeSizeChartKebabMenu,
    setSelectedVariant,
    updateInventoryProducts,
} = inventoryManagementSlice.actions;

export {
    fetchInventoryProducts,
    fetchInventoryProduct,
    fetchSizeCharts,
    fetchSizeChart,
}

export default inventoryManagementSlice.reducer;
